import React, { Suspense, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './PrivateRoute';
import LoadingManager from './context/LoadingManager';
import Loading from './components/Loading';
import Popup from './components/Popup';
import './ i18n';
import usePopupStore from './store/popupStore';
import { Toast } from './components/Toastify';

const Login = React.lazy(() => import('./auth/Login'));
const ForgotPassword = React.lazy(() => import('./auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./auth/ResetPassword'));
const Dashboard = React.lazy(() => import('./dashboard/Index'));
const Clubs = React.lazy(() => import('./clubs/Clubs'));
const Machines = React.lazy(() => import('./clubs/Machines'));
const Customers = React.lazy(() => import('./clubs/Customers'));
const Quiz = React.lazy(() => import('./quiz/index'));
const LiveScoring = React.lazy(() => import('./liveScoring/index'));
const PaymentConfigs = React.lazy(() => import('./payment/config/index'));

const App: React.FC = () => {
  // The value is never read; the state exists so a loading change re-renders.
  const [, setIsLoading] = useState<boolean>(LoadingManager.getLoading());

  useEffect(() => {
    const handleLoadingChange = (status: boolean) => {
      setIsLoading(status);
    };

    LoadingManager.on('loadingChange', handleLoadingChange);
    return () => {
      LoadingManager.off('loadingChange', handleLoadingChange);
    };
  }, []);

  const { isPopup } = usePopupStore();

  return (
    <AuthProvider>
      <Router>
        {isPopup && <Popup />}
        <Toast />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route element={<PrivateRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clubs" element={<Clubs />} />
              <Route path="/clubs/:id" element={<Clubs />} />
              <Route path="/clubs/:id/machines" element={<Machines />} />
              <Route path="/clubs/:id/customers" element={<Customers />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/quiz/:id" element={<Quiz />} />
              <Route path="/quiz/:id/edit" element={<Quiz />} />
              <Route path="/quiz/:id/delete" element={<Quiz />} />
              <Route path="/livescoring" element={<LiveScoring />} />
              <Route path="/livescoring/:id/player" element={<LiveScoring />} />
              <Route path="/livescoring/:id/round" element={<LiveScoring />} />
              <Route path="/livescoring/:id/cut" element={<LiveScoring />} />
              <Route path="/livescoring/:id/device" element={<LiveScoring />} />
              <Route path="/livescoring/:id/status" element={<LiveScoring />} />
              <Route path="/livescoring/:id/info" element={<LiveScoring />} />
              <Route path="/livescoring/:id/course" element={<LiveScoring />} />
              <Route path="/payment/config" element={<PaymentConfigs />} />
              <Route
                path="/payment/config/:id/edit"
                element={<PaymentConfigs />}
              />
            </Route>
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
};

export default App;
